import type { MessageKey } from "@gioia365/i18n";
import type { AppRouter } from "../../router";
import type { MenuSubject } from "../../session/access";

// Le sezioni delle Impostazioni (piano M1b D15), in un posto solo: le leggono il menu delle sezioni (`SettingsLayout`), la
// voce «Impostazioni» della rail e la Vista Commerciale, che apre soltanto le pagine del menu del Commerciale.
//
// Chi le vede. Fuori dalla vista ogni persona attiva vede Team, «Ruoli e permessi», «Campi della scheda», «Invio dei WhatsApp» e
// «Modelli WhatsApp» (M6), «Calendario, fuso e lingua», «Chi vede i Lead», «Sequenza di chiamate» (M5), «Ingressi dei Lead» e
// «Assegnazione dei Lead» (M6), in sola lettura se non può cambiarle (ipotesi 7 dell'interfaccia di M1b, come i mock-up e come il server, che apre la
// lettura a ogni appartenenza attiva); «Importazione ed esportazione» soltanto chi ha `import_leads` e il Super admin di Gioia365
// (questione aperta 125); «Obiettivi» (M7) soltanto chi ha `settings.goals` e il Super admin di Gioia365. Modificare richiede la funzione che il server controlla, e lo decide ogni sezione.
//
// Nella Vista Commerciale (decisione del product owner del 2 ottobre 2026) il menu è quello del Commerciale scelto: una
// sezione c'è soltanto se il suo ruolo la ha fra i permessi (`roleKeys`: la sezione a spunta del catalogo, oppure la
// funzione che la gestisce), mai per la sola lettura che ogni persona attiva ha. Così uno User di partenza, che non ne ha
// nessuna, nella vista non ha le Impostazioni (interpretazione di Claude, domanda aperta 118).

export type SettingsSectionId =
  | "billing"
  | "team"
  | "roles"
  | "leadFields"
  | "whatsappSending"
  | "whatsappTemplates"
  | "calendar"
  | "leadVisibility"
  | "callSequence"
  | "goals"
  | "leadIntake"
  | "assignment"
  | "importExport"
  | "connections";

/** I gruppi del menu, col loro titolo piccolo (IMP-13). */
export type SettingsGroupId = "company" | "people" | "leads" | "calls" | "whatsapp" | "data";

export type SettingsSection = {
  id: SettingsSectionId;
  to: keyof AppRouter["routesByPath"];
  label: MessageKey;
  /** Il gruppo del menu in cui sta la sezione. */
  group: SettingsGroupId;
  /** I permessi del ruolo che portano la sezione nel suo menu: basta uno. */
  roleKeys: readonly string[];
  /** Ogni persona attiva la vede fuori dalla Vista Commerciale, in sola lettura se non ha `roleKeys`. */
  everyone: boolean;
  /** La porta nel menu anche un ruolo di questo livello o più alto, senza `roleKeys`: «Collegamenti», da Admin (3) in su. */
  minLevel?: number;
};

export type SettingsGroup = { id: SettingsGroupId; label: MessageKey; sections: SettingsSection[] };

// I gruppi nell'ordine del menu. «Dati» in fondo: lì sta «Importazione ed esportazione», ultima sezione (IMP-9).
export const SETTINGS_GROUPS: readonly { id: SettingsGroupId; label: MessageKey }[] = [
  { id: "company", label: "settings.groups.company" },
  { id: "people", label: "settings.groups.people" },
  { id: "leads", label: "settings.groups.leads" },
  { id: "calls", label: "settings.groups.calls" },
  { id: "whatsapp", label: "settings.groups.whatsapp" },
  { id: "data", label: "settings.groups.data" },
];

// Nell'ordine a gruppi approvato da Lucio il 17 ottobre 2026 (IMP-13): Azienda, Persone, Lead, Chiamate, WhatsApp, Dati. Chi
// vede una sezione non cambia con l'ordine; le sezioni di un gruppo stanno una dopo l'altra, come i gruppi in `SETTINGS_GROUPS`.
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  { id: "calendar", to: "/impostazioni/calendario-fuso-e-lingua", label: "settings.calendar", group: "company", roleKeys: ["settings.calendar"], everyone: true },
  { id: "billing", to: "/abbonamento", label: "billing.manageTitle", group: "company", roleKeys: [], everyone: false },
  // «Collegamenti» (M7 parte 2): lo stato di ogni collegamento dell'azienda, in sola lettura, per l'Admin e il Super admin dell'azienda
  // (livello 3 o più, come il server) e per il Super admin di Gioia365; nella Vista Commerciale soltanto se la persona osservata ha quel
  // livello. Nessuna funzione nuova del catalogo: i ruoli Admin delle aziende che ci sono già la vedono senza cambiare niente.
  { id: "connections", to: "/impostazioni/collegamenti", label: "connections.menu", group: "company", roleKeys: [], everyone: false, minLevel: 3 },
  { id: "team", to: "/impostazioni/team", label: "settings.team", group: "people", roleKeys: ["settings.team", "manage_team"], everyone: true },
  { id: "roles", to: "/impostazioni/ruoli-e-permessi", label: "settings.roles", group: "people", roleKeys: ["settings.roles", "manage_roles"], everyone: true },
  { id: "leadVisibility", to: "/impostazioni/chi-vede-i-lead", label: "settings.leadVisibility", group: "people", roleKeys: ["settings.lead_visibility"], everyone: true },
  { id: "leadFields", to: "/impostazioni/campi-della-scheda", label: "settings.leadFields", group: "leads", roleKeys: ["settings.lead_fields"], everyone: true },
  // «Ingressi dei Lead» prima di «Assegnazione dei Lead» (piano dell'ingresso del 10 ottobre 2026, I2, Task 4): la vede ogni persona
  // attiva, la cambia `settings.lead_intake` (di partenza il Super admin). «Assegnazione dei Lead» (piano M6, Task 13): la vede ogni
  // persona attiva, la cambia `settings.assignment`.
  { id: "leadIntake", to: "/impostazioni/ingressi-dei-lead", label: "settings.leadIntake", group: "leads", roleKeys: ["settings.lead_intake"], everyone: true },
  { id: "assignment", to: "/impostazioni/assegnazione-dei-lead", label: "settings.assignment", group: "leads", roleKeys: ["settings.assignment"], everyone: true },
  // «Sequenza di chiamate» (piano M5, Task 13): la vede ogni persona attiva, la cambia `settings.call_sequence`.
  { id: "callSequence", to: "/impostazioni/sequenza-di-chiamate", label: "settings.callSequence", group: "calls", roleKeys: ["settings.call_sequence"], everyone: true },
  // «Obiettivi» (piano M7, Task 6; D9, SEZ-16): dopo «Sequenza di chiamate», come il mock-up approvato del menu (IMP-13). La vede
  // soltanto chi ha `settings.goals` (di partenza il Super admin), perché mostra l'obiettivo in euro di ogni persona: non è fra le
  // sezioni che ogni persona attiva legge.
  { id: "goals", to: "/impostazioni/obiettivi", label: "settings.goals", group: "calls", roleKeys: ["settings.goals"], everyone: false },
  // I WhatsApp (piano M6, R1, Task 5a). «Invio dei WhatsApp» la portano la sua sezione, la scelta dei WhatsApp dell'azienda
  // (`settings.whatsapp_channels`, questione 403) e «Gestire il team», che imposta il WhatsApp delle persone (questione 405);
  // «Modelli WhatsApp» la sua sezione.
  {
    id: "whatsappSending",
    to: "/impostazioni/invio-dei-whatsapp",
    label: "settings.whatsappSending",
    group: "whatsapp",
    roleKeys: ["settings.whatsapp_sending", "settings.whatsapp_channels", "manage_team"],
    everyone: true,
  },
  { id: "whatsappTemplates", to: "/impostazioni/modelli-whatsapp", label: "settings.whatsappTemplates", group: "whatsapp", roleKeys: ["settings.whatsapp_templates"], everyone: true },
  { id: "importExport", to: "/impostazioni/importazione-ed-esportazione", label: "settings.importExport", group: "data", roleKeys: ["import_leads"], everyone: false },
];

/** Le sezioni nel menu di `subject`, nell'ordine del menu; nessuna senza un'azienda. */
export function settingsSectionsOf(subject: MenuSubject): SettingsSection[] {
  const { access, observed } = subject;
  if (!access) return [];
  if(access.context.billingRecovery)return !observed&&!access.isPlatform&&access.context.isProtectedOwner===true ? SETTINGS_SECTIONS.filter(section=>section.id==="team"||section.id==="billing") : [];
  return SETTINGS_SECTIONS.filter(
    (section) =>
      section.id === "billing"
        ? !observed && !access.isPlatform && access.context.isProtectedOwner === true
        : access.isPlatform ||
          section.roleKeys.some((key) => access.can(key)) ||
          (section.minLevel !== undefined && (access.context.role?.level ?? 0) >= section.minLevel) ||
          (section.everyone && !observed),
  );
}

/** Le sezioni di un menu divise nei loro gruppi, nell'ordine del menu; un gruppo senza sezioni visibili non c'è, e neanche il suo titolo. */
export function settingsGroupsOf(sections: readonly SettingsSection[]): SettingsGroup[] {
  return SETTINGS_GROUPS.map((group) => ({ ...group, sections: sections.filter((section) => section.group === group.id) })).filter((group) => group.sections.length > 0);
}

/** La sezione di una pagina delle Impostazioni (anche una pagina dentro la sezione), oppure `undefined`. */
export function settingsSectionAt(pathname: string): SettingsSection | undefined {
  return SETTINGS_SECTIONS.find((section) => pathname === section.to || pathname.startsWith(`${section.to}/`));
}
